/**
 * @fileoverview Session Templates List Component
 * @created 10-10-2025 19:49:52
 * 
 * Reusable table component for displaying session templates with usage statistics.
 * Used in Building Management and Planner drawer.
 */

import React from 'react';
import {
  Table,
  Group,
  Badge,
  Text,
  ActionIcon,
  Menu,
  Tooltip,
  Box,
  Stack,
  Paper
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconClock,
  IconDots,
  IconBuilding
} from '@tabler/icons-react';
import type { SessionTemplate } from '../../../types/session-templates';

export interface SessionTemplatesListProps {
  templates: SessionTemplate[];
  onEdit: (template: SessionTemplate) => void;
  onDelete: (id: string) => void;
  loading?: boolean;
  compact?: boolean;
}

/**
 * Session Templates List Component
 * Displays templates with usage stats and actions
 */
export function SessionTemplatesList({
  templates,
  onEdit,
  onDelete,
  loading = false,
  compact = false
}: SessionTemplatesListProps): JSX.Element {
  
  /**
   * Calculate duration in minutes
   */
  const calculateDuration = (template: SessionTemplate): string => {
    const start = template.startTime.split(':').map(Number);
    const end = template.endTime.split(':').map(Number);
    const startMinutes = start[0] * 60 + start[1];
    const endMinutes = end[0] * 60 + end[1];
    const duration = endMinutes - startMinutes;
    
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  /**
   * Check if template can be deleted
   */
  const canDelete = (template: SessionTemplate): boolean => {
    const roomCount = template._count?.roomTemplates || 0;
    const assignmentCount = template._count?.assignments || 0;
    return roomCount === 0 && assignmentCount === 0;
  };

  if (templates.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" gap="md">
          <IconClock size={48} color="#94a3b8" />
          <Text c="dimmed" size="lg">No session templates found</Text>
          <Text c="dimmed" size="sm">Create your first template to get started</Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table striped highlightOnHover verticalSpacing={compact ? "xs" : "sm"}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Template Name</Table.Th>
            <Table.Th>Time Range</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th>Buffer</Table.Th>
            <Table.Th>Assigned Rooms</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {templates.map((template) => (
            <Table.Tr key={template.id}>
              {/* Template Name */}
              <Table.Td>
                <Stack gap={4}>
                  <Text fw={500} size="sm">{template.name}</Text>
                  {template.description && (
                    <Text size="xs" c="dimmed">{template.description}</Text>
                  )}
                </Stack>
              </Table.Td>

              {/* Time Range */}
              <Table.Td>
                <Group gap="xs">
                  <IconClock size={14} color="#64748b" />
                  <Text size="sm">{template.startTime} - {template.endTime}</Text>
                </Group>
              </Table.Td>

              {/* Duration */}
              <Table.Td>
                <Text size="sm">{calculateDuration(template)}</Text>
              </Table.Td>

              {/* Buffer */}
              <Table.Td>
                <Text size="sm">
                  {template.bufferMinutes > 0 ? `${template.bufferMinutes} min` : '-'}
                </Text>
              </Table.Td>

              {/* Assigned Rooms Count */}
              <Table.Td>
                <Group gap="xs">
                  <IconBuilding size={14} color="#64748b" />
                  <Badge variant="light" color="blue" size="sm">
                    {template._count?.roomTemplates || 0} rooms
                  </Badge>
                </Group>
              </Table.Td>

              {/* Status */}
              <Table.Td>
                <Badge
                  variant="dot"
                  color={template.isActive ? 'green' : 'gray'}
                  size="sm"
                >
                  {template.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Table.Td>

              {/* Actions */}
              <Table.Td>
                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      onClick={() => onEdit(template)}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Divider />
                    <Tooltip
                      label={
                        canDelete(template)
                          ? 'Delete template'
                          : 'Cannot delete: Template is used by rooms or assignments'
                      }
                      position="left"
                    >
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => onDelete(template.id)}
                        disabled={!canDelete(template)}
                      >
                        Delete
                      </Menu.Item>
                    </Tooltip>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

